import { p } from "./_helpers";

const energie = {
 slug: "energie",
 path: "/secteurs/energie",
 hero: {
 image: "/photos/sector-energie-hero.jpg",
 alt: p("Panneaux solaires en Guinée-Bissau", "Solar panels in Guinea-Bissau", "Painéis solares na Guiné-Bissau"),
 breadcrumb: p("Énergies renouvelables", "Renewable energy", "Energias renováveis"),
 title: p("Investir dans l'énergie solaire.", "Investing in solar energy.", "Investir na energia solar."),
 subtitle: p(
 "L'un des potentiels solaires les plus élevés d'Afrique de l'Ouest, encore très largement inexploité.",
 "One of the highest solar potentials in West Africa, still largely untapped.",
 "Um dos maiores potenciais solares da África Ocidental, ainda largamente por explorar."
 ),
 },
 seo: {
 title: p(
 "Investir dans l'énergie solaire en Guinée-Bissau | NANDA SÁ",
 "Investing in solar energy in Guinea-Bissau | NANDA SÁ",
 "Investir na energia solar na Guiné-Bissau | NANDA SÁ"
 ),
 description: p(
 "Panorama du solaire en Guinée-Bissau : ensoleillement, mini-réseaux ruraux, pompage, stockage lithium. Chiffres clés, opportunités, défis et passerelles NANDA SÁ.",
 "Panorama of solar in Guinea-Bissau: sunshine, rural mini-grids, pumping, lithium storage. Key figures, opportunities, challenges and NANDA SÁ pathways.",
 "Panorama do solar na Guiné-Bissau: insolação, mini-redes rurais, bombagem, armazenamento de lítio. Dados-chave, oportunidades, desafios e pontes NANDA SÁ."
 ),
 keywords: "énergie solaire Guinée-Bissau, mini-réseau Afrique de l'Ouest, pompage solaire, stockage lithium, électrification rurale",
 },
 presentation: {
 title: p("Le soleil comme ressource première.", "The sun as first resource.", "O sol como recurso primordial."),
 paragraphs: p(
 [
 "La Guinée-Bissau bénéficie de 2 500 à 3 000 heures d'ensoleillement annuel, soit l'un des potentiels solaires les plus élevés d'Afrique de l'Ouest. Pourtant, le taux d'accès à l'électricité reste inférieur à 40 %, avec des écarts marqués entre Bissau et les zones rurales.",
 "Cette situation, régulièrement documentée par la Banque mondiale, l'AFD, la BAD et l'IRENA, ouvre un espace d'investissement immédiat : solaire résidentiel, professionnel, mini-réseaux ruraux, pompage agricole, éclairage public, stockage lithium.",
 ],
 [
 "Guinea-Bissau enjoys 2,500 to 3,000 hours of annual sunshine, one of the highest solar potentials in West Africa. Yet the electricity access rate remains below 40%, with sharp contrasts between Bissau and rural areas.",
 "This situation, regularly documented by the World Bank, AFD, AfDB and IRENA, opens up an immediate investment window: residential and professional solar, rural mini-grids, agricultural pumping, street lighting, lithium storage.",
 ],
 [
 "A Guiné-Bissau beneficia de 2 500 a 3 000 horas de insolação anual, um dos maiores potenciais solares da África Ocidental. Contudo, a taxa de acesso à electricidade permanece inferior a 40%, com fortes contrastes entre Bissau e as zonas rurais.",
 "Esta situação, regularmente documentada pelo Banco Mundial, AFD, BAD e IRENA, abre um espaço de investimento imediato: solar residencial e profissional, mini-redes rurais, bombagem agrícola, iluminação pública, armazenamento de lítio.",
 ]
 ),
 },
 kpis: {
 title: p("Chiffres clés du solaire", "Solar key figures", "Dados-chave do solar"),
 items: [
 { n: "2 800h", l: p("Ensoleillement annuel", "Annual sunshine", "Insolação anual"), d: p("Parmi les plus élevés de la sous-région.", "Among the highest in the sub-region.", "Entre os maiores da sub-região.") },
 { n: "< 40 %", l: p("Taux d'accès électrique", "Electricity access rate", "Taxa de acesso eléctrico"), d: p("Marge de progression majeure hors capitale.", "Major room for growth outside the capital.", "Forte margem de progressão fora da capital.") },
 { n: "5,5 kWh/m²", l: p("Irradiation quotidienne moyenne", "Average daily irradiation", "Irradiação diária média"), d: p("Rendement idéal pour le photovoltaïque.", "Ideal yield for photovoltaic.", "Rendimento ideal para o fotovoltaico.") },
 { n: "70 %+", l: p("Ruraux hors réseau", "Rural off-grid", "Rurais fora da rede"), d: p("Cible naturelle des mini-réseaux et du solar home.", "Natural target for mini-grids and solar home.", "Alvo natural para mini-redes e solar home.") },
 { n: "0,25-0,40 $", l: p("Coût du kWh diesel", "Diesel kWh cost", "Custo do kWh a diesel"), d: p("Fossile encore dominant, remplaçable par solaire + stockage.", "Fossil still dominant, replaceable by solar + storage.", "Fóssil ainda dominante, substituível por solar + armazenamento.") },
 { n: "0,08-0,12 $", l: p("Coût du kWh solaire projeté", "Projected solar kWh cost", "Custo do kWh solar projectado"), d: p("Compétitivité économique déjà acquise.", "Economic competitiveness already established.", "Competitividade económica já adquirida.") },
 ],
 sources: p(
 "Sources : IRENA, Banque mondiale (ESMAP), AFD, BAD, statistiques nationales 2023-2025.",
 "Sources: IRENA, World Bank (ESMAP), AFD, AfDB, national statistics 2023-2025.",
 "Fontes: IRENA, Banco Mundial (ESMAP), AFD, BAD, estatísticas nacionais 2023-2025."
 ),
 },
 opportunites: {
 title: p("Segments d'investissement prioritaires", "Priority investment segments", "Segmentos de investimento prioritários"),
 intro: p(
 "Quatre segments distincts, chacun avec sa maturité, son ticket d'entrée et son horizon de retour.",
 "Four distinct segments, each with its maturity, entry ticket and return horizon.",
 "Quatro segmentos distintos, cada um com a sua maturidade, ticket de entrada e horizonte de retorno."
 ),
 segments: [
 { h3: p("1. Solaire résidentiel & professionnel (Bissau + Canchungo)", "1. Residential & professional solar (Bissau + Canchungo)", "1. Solar residencial e profissional (Bissau + Canchungo)"),
 p: p(
 "Marché urbain à forte croissance. Panneaux, onduleurs, batteries lithium (MARSTEK Venus A, Q.SAVE-G3), installation clé en main. Ticket : 20 000 à 200 000 €. ROI 3-5 ans.",
 "Fast-growing urban market. Panels, inverters, lithium batteries (MARSTEK Venus A, Q.SAVE-G3), turnkey installation. Ticket: €20k to €200k. ROI 3-5 years.",
 "Mercado urbano de forte crescimento. Painéis, inversores, baterias de lítio (MARSTEK Venus A, Q.SAVE-G3), instalação chave na mão. Ticket: 20 000 a 200 000 €. ROI 3-5 anos."
 ) },
 { h3: p("2. Mini-réseaux ruraux (Cacheu, Oio, Bafatá)", "2. Rural mini-grids (Cacheu, Oio, Bafatá)", "2. Mini-redes rurais (Cacheu, Oio, Bafatá)"),
 p: p(
 "Modèle éprouvé en Afrique de l'Ouest, soutenu par la Banque mondiale et l'AFD. Concession sur 15-20 ans, tarifs régulés. Ticket : 200 000 à 2 M€ par site.",
 "Model proven in West Africa, backed by the World Bank and AFD. 15-20 year concessions, regulated tariffs. Ticket: €200k to €2M per site.",
 "Modelo comprovado em África Ocidental, apoiado pelo Banco Mundial e pela AFD. Concessão de 15-20 anos, tarifas reguladas. Ticket: 200 000 a 2 M€ por local."
 ) },
 { h3: p("3. Pompage solaire agricole & hydraulique", "3. Solar agricultural & water pumping", "3. Bombagem solar agrícola e hidráulica"),
 p: p(
 "Adossé aux besoins d'irrigation cajou, riz, maraîchage, et d'accès à l'eau potable. Kits standardisés, marges régulières. Ticket : 5 000 à 50 000 € par installation.",
 "Backed by irrigation needs for cashew, rice, market gardening, and drinking water access. Standardised kits, regular margins. Ticket: €5k to €50k per installation.",
 "Ligado às necessidades de irrigação do caju, arroz, horticultura, e ao acesso à água potável. Kits padronizados, margens regulares. Ticket: 5 000 a 50 000 € por instalação."
 ) },
 { h3: p("4. Stockage lithium & backup solaire pour industrie", "4. Lithium storage & solar backup for industry", "4. Armazenamento de lítio e backup solar para indústria"),
 p: p(
 "Toute la chaîne agroalimentaire (cajou, riz, poisson) a besoin d'énergie stable pour la transformation et la chaîne du froid. Marché B2B à forte valeur.",
 "The entire agri-food chain (cashew, rice, fish) needs stable power for processing and cold chain. High-value B2B market.",
 "Toda a cadeia agroalimentar (caju, arroz, peixe) precisa de energia estável para transformação e cadeia de frio. Mercado B2B de alto valor."
 ) },
 ],
 },
 defis: {
 title: p("Les défis à anticiper", "Challenges to anticipate", "Desafios a antecipar"),
 intro: p(
 "Le solaire est mûr techniquement et économiquement, mais son déploiement en Guinée-Bissau suppose de prendre en compte plusieurs facteurs.",
 "Solar is technically and economically mature, but its deployment in Guinea-Bissau requires taking several factors into account.",
 "O solar está tecnicamente e economicamente maduro, mas o seu desdobramento na Guiné-Bissau exige levar em conta vários factores."
 ),
 items: p(
 [
 "Un cadre régulatoire encore en structuration, notamment sur les concessions mini-réseaux.",
 "Un tissu bancaire local peu familier des financements longs (15+ ans) des projets d'électrification.",
 "Un service après-vente technique à construire — le SAV solaire devient une opportunité en soi.",
 "Une logistique d'importation d'équipements dépendante des ports de Bissau.",
 "Un besoin de formation technique locale (installateurs, techniciens, chef de site).",
 ],
 [
 "A regulatory framework still under construction, particularly on mini-grid concessions.",
 "A local banking sector unfamiliar with the long-term (15+ years) financing of electrification projects.",
 "A technical after-sales service to build — solar maintenance becomes an opportunity in itself.",
 "Equipment import logistics dependent on the port of Bissau.",
 "A need for local technical training (installers, technicians, site managers).",
 ],
 [
 "Um enquadramento regulatório ainda em estruturação, nomeadamente sobre as concessões de mini-redes.",
 "Um sector bancário local pouco familiarizado com financiamentos longos (15+ anos) de projectos de electrificação.",
 "Um serviço pós-venda técnico a construir — a manutenção solar torna-se uma oportunidade em si.",
 "Uma logística de importação de equipamentos dependente dos porto autónomo de Bissau.",
 "Uma necessidade de formação técnica local (instaladores, técnicos, chefes de estação).",
 ]
 ),
 closing: p(
 "Anticiper ces défis, c'est déjà se positionner en tête. C'est ce que fait NANDA SÁ Energia Pro depuis 2023.",
 "Anticipating these challenges means positioning yourself at the front. This is what NANDA SÁ Energia Pro has been doing since 2023.",
 "Antecipar estes desafios é já posicionar-se à frente. É o que a NANDA SÁ Energia Pro faz desde 2023."
 ),
 },
 pourquoi: {
 title: p("Un pôle solaire opérationnel depuis 2023.", "An operational solar unit since 2023.", "Um pólo solar operacional desde 2023."),
 paragraphs: p(
 [
 "NANDA SÁ Energia Pro déploie déjà les solutions décrites dans cette page : installations résidentielles et professionnelles à Bissau, systèmes agricoles à Cacheu, partenariats avec Qcells (panneaux Q.SAVE-G3) et MARSTEK (batteries Venus A).",
 "Notre équipe technique locale, formée à l'international, garantit une exécution rigoureuse et un service après-vente sur la durée. Nous accompagnons également les investisseurs souhaitant lancer un projet solaire d'échelle en Guinée-Bissau.",
 ],
 [
 "NANDA SÁ Energia Pro already deploys the solutions described on this page: residential and professional installations in Bissau, agricultural systems in Cacheu, partnerships with Qcells (Q.SAVE-G3 panels) and MARSTEK (Venus A batteries).",
 "Our local technical team, internationally trained, guarantees rigorous execution and long-term after-sales service. We also support investors wishing to launch a large-scale solar project in Guinea-Bissau.",
 ],
 [
 "A NANDA SÁ Energia Pro já implementa as soluções descritas nesta página: instalações residenciais e profissionais em Bissau, sistemas agrícolas em Cacheu, parcerias com a Qcells (painéis Q.SAVE-G3) e a MARSTEK (baterias Venus A).",
 "A nossa equipa técnica local, formada internacionalmente, garante uma execução rigorosa e um serviço pós-venda duradouro. Acompanhamos também os investidores que desejam lançar um projecto solar de escala na Guiné-Bissau.",
 ]
 ),
 bullets: p(
 [
 "Installation solaire résidentielle et professionnelle clé en main",
 "Mini-réseaux ruraux et solutions d'électrification décentralisée",
 "Pompage solaire agricole et hydraulique",
 "Éclairage public et stockage lithium industriel",
 "SAV local, contrats de maintenance longue durée",
 ],
 [
 "Turnkey residential and professional solar installation",
 "Rural mini-grids and decentralised electrification solutions",
 "Solar agricultural and water pumping",
 "Street lighting and industrial lithium storage",
 "Local after-sales service, long-term maintenance contracts",
 ],
 [
 "Instalação solar residencial e profissional chave na mão",
 "Mini-redes rurais e soluções de electrificação descentralizada",
 "Bombagem solar agrícola e hidráulica",
 "Iluminação pública e armazenamento industrial de lítio",
 "Serviço pós-venda local, contratos de manutenção de longa duração",
 ]
 ),
 },
 download: {
 title: p(
 "Télécharger la note « Énergie solaire · Guinée-Bissau ».",
 "Download the brief « Solar energy · Guinea-Bissau ».",
 "Descarregar a nota « Energia solar · Guiné-Bissau »."
 ),
 body: p(
 "Une note de synthèse préparée par NANDA SÁ : chiffres clés, segments d'investissement, cadre régulatoire et contacts utiles.",
 "A concise brief prepared by NANDA SÁ: key figures, investment segments, regulatory framework and useful contacts.",
 "Uma nota de síntese preparada pela NANDA SÁ: dados-chave, segmentos de investimento, enquadramento regulatório e contactos úteis."
 ),
 pdfUrl: "/pdf/nanda-sa-note-energie.pdf",
 pdfFilename: "nanda-sa-note-energie.pdf",
 mailSubject: p("Téléchargement de la note « Énergie solaire · Guinée-Bissau »", "Download of the brief « Solar energy · Guinea-Bissau »", "Descarga da nota « Energia solar · Guiné-Bissau »"),
 mailBody: p(
 "Bonjour, je souhaite recevoir la note « Énergie solaire · Guinée-Bissau ». Merci de me tenir informé(e) des prochaines publications de NANDA SÁ.",
 "Hello, I would like to receive the brief « Solar energy · Guinea-Bissau ». Please keep me informed of NANDA SÁ's next publications.",
 "Olá, gostaria de receber a nota « Energia solar · Guiné-Bissau ». Por favor, mantenha-me informado(a) das próximas publicações da NANDA SÁ."
 ),
 },
 contact: {
 title: p("Parlons de votre projet solaire.", "Let's talk about your solar project.", "Falemos do seu projecto solar."),
 body: p(
 "Investisseur, promoteur de mini-réseau, industriel, coopérative ou institution : décrivez votre intention, nous répondons personnellement.",
 "Investor, mini-grid promoter, industrial, cooperative or institution: describe your intention, we respond personally.",
 "Investidor, promotor de mini-rede, industrial, cooperativa ou instituição: descreva a sua intenção, respondemos pessoalmente."
 ),
 quote: p(
 "Le soleil est la ressource la plus démocratique. Encore faut-il la structurer.",
 "The sun is the most democratic resource. It still has to be structured.",
 "O sol é o recurso mais democrático. Ainda assim, é preciso estruturá-lo."
 ),
 subject: p("Investir dans le solaire en Guinée-Bissau", "Invest in solar in Guinea-Bissau", "Investir no solar na Guiné-Bissau"),
 },
 ctas: ["Energia", "Conseil"],
};

export default energie;
